import {
  Badge,
  Box,
  Divider,
  Flex,
  Grid,
  Heading,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack
} from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { ElementType, FC } from 'react'
import { BsCalculator } from 'react-icons/bs'
import { FiTrendingUp } from 'react-icons/fi'
import { MdOutlineShoppingCart } from 'react-icons/md'

interface CalculoDetalheModalProps {
  isOpen: boolean
  onClose: () => void
  produto: string
  centro: string
  etapasSaida: string[]
  etapasQuantidade: string[]
}

const BRAND_GREEN = '#1d5840'
const BRAND_YELLOW = '#ECC94B'

// ── Variantes de animação ──────────────────────────────────────────────────────

const listVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.07,
      delayChildren: 0.05
    }
  }
}

const cardVariants = {
  hidden: { opacity: 0, y: 12, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.22, ease: 'easeOut' }
  }
}

const headerVariants = {
  hidden: { opacity: 0, x: -8 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.2, ease: 'easeOut' } }
}

// ── StepCard ──────────────────────────────────────────────────────────────────

const StepCard: FC<{
  step: string
  index: number
  total: number
  colorScheme: 'blue' | 'green'
}> = ({ step, index, total, colorScheme }) => {
  const isLast = index === total - 1

  const colors = {
    blue: {
      bg: isLast ? '#EBF8FF' : 'white',
      border: isLast ? '#3182CE' : '#E2E8F0',
      badgeBg: isLast ? '#3182CE' : '#EBF8FF',
      badgeColor: isLast ? 'white' : '#3182CE',
      textColor: isLast ? '#1A365D' : '#2D3748'
    },
    green: {
      bg: isLast ? '#F0FFF4' : 'white',
      border: isLast ? BRAND_GREEN : '#E2E8F0',
      badgeBg: isLast ? BRAND_GREEN : '#F0FFF4',
      badgeColor: isLast ? 'white' : BRAND_GREEN,
      textColor: isLast ? '#1C4532' : '#2D3748'
    }
  }
  const c = colors[colorScheme]

  return (
    <motion.div variants={cardVariants}>
      <Flex
        align="flex-start"
        gap={5}
        bg={c.bg}
        border="1px solid"
        borderColor={c.border}
        borderRadius="10px"
        px={5}
        py={3}
        boxShadow={isLast ? 'sm' : 'none'}
      >
        {/* Número / check */}
        <Flex
          flexShrink={0}
          w="28px"
          h="28px"
          borderRadius="full"
          bg={c.badgeBg}
          color={c.badgeColor}
          align="center"
          justify="center"
          fontSize="11px"
          fontWeight="800"
          border="1px solid"
          borderColor={c.border}
          mt="1px"
        >
          {isLast ? '✓' : index + 1}
        </Flex>

        <Text
          ml={2}
          fontSize="12.5px"
          color={c.textColor}
          fontWeight={isLast ? '700' : '400'}
          lineHeight="1.6"
          flex={1}
        >
          {step}
        </Text>

        {isLast && (
          <Badge
            colorScheme={colorScheme === 'blue' ? 'blue' : 'green'}
            variant="solid"
            fontSize="9px"
            px={2}
            py={0.5}
            borderRadius="full"
            flexShrink={0}
            alignSelf="center"
          >
            RESULTADO
          </Badge>
        )}
      </Flex>
    </motion.div>
  )
}

// ── SectionHeader ─────────────────────────────────────────────────────────────

const SectionHeader: FC<{
  icon: ElementType
  title: string
  subtitle: string
  color: string
  bg: string
}> = ({ icon, title, subtitle, color, bg }) => (
  <motion.div variants={headerVariants}>
    <Flex align="center" gap={3} mb={4}>
      <Flex
        w="40px"
        h="40px"
        borderRadius="10px"
        bg={bg}
        align="center"
        justify="center"
        flexShrink={0}
      >
        <Icon as={icon} color={color} boxSize={5} />
      </Flex>
      <Box>
        <Heading fontSize="14px" fontWeight="700" color="gray.800">
          {title}
        </Heading>
        <Text fontSize="11px" color="gray.500" mt="1px">
          {subtitle}
        </Text>
      </Box>
    </Flex>
  </motion.div>
)

// ── EmptySteps ────────────────────────────────────────────────────────────────

const EmptySteps: FC<{ label: string }> = ({ label }) => (
  <motion.div variants={cardVariants}>
    <Flex
      align="center"
      justify="center"
      h="80px"
      borderRadius="10px"
      border="1px dashed"
      borderColor="gray.200"
      bg="gray.50"
    >
      <Text fontSize="12px" color="gray.400" fontStyle="italic">
        {label}
      </Text>
    </Flex>
  </motion.div>
)

// ── StepList animada ──────────────────────────────────────────────────────────

const StepList: FC<{
  steps: string[]
  colorScheme: 'blue' | 'green'
  animationKey: string
}> = ({ steps, colorScheme, animationKey }) => (
  <AnimatePresence>
    <motion.div
      key={animationKey}
      variants={listVariants}
      initial="hidden"
      animate="visible"
    >
      <VStack spacing={2} align="stretch">
        {steps.length > 0 ? (
          steps.map((step, i) => (
            <StepCard
              key={i}
              step={step}
              index={i}
              total={steps.length}
              colorScheme={colorScheme}
            />
          ))
        ) : (
          <EmptySteps label="Sem etapas disponíveis para esta linha" />
        )}
      </VStack>
    </motion.div>
  </AnimatePresence>
)

// ── Modal principal ───────────────────────────────────────────────────────────

const CalculoDetalheModal: FC<CalculoDetalheModalProps> = ({
  isOpen,
  onClose,
  produto,
  centro,
  etapasSaida,
  etapasQuantidade
}) => {
  // Chave muda a cada abertura para re-disparar a animação
  const animKey = isOpen ? `open-${produto}-${centro}` : 'closed'

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      scrollBehavior="inside"
      motionPreset="slideInBottom"
      size="5xl"
    >
      <ModalOverlay backdropFilter="blur(2px)" bg="blackAlpha.400" />
      <ModalContent
        bg="white"
        borderRadius="16px"
        overflow="hidden"
        mx={[4, 8]}
        maxH="90vh"
      >
        {/* ── Header ── */}
        <ModalHeader
          p={0}
          style={{
            background: `linear-gradient(135deg, ${BRAND_GREEN} 0%, #2a7a58 100%)`
          }}
        >
          <Flex align="center" px={5} py={4} gap={3}>
            <Flex
              w="38px"
              h="38px"
              borderRadius="9px"
              bg="whiteAlpha.200"
              align="center"
              justify="center"
              flexShrink={0}
            >
              <Icon as={BsCalculator} color="white" boxSize={5} />
            </Flex>
            <Box>
              <Heading fontSize="15px" color="white" fontWeight="700">
                Detalhamento do Cálculo
              </Heading>
              <Flex align="center" gap={2} mt="2px">
                <Badge
                  bg="whiteAlpha.300"
                  color="white"
                  fontSize="10px"
                  px={2}
                  borderRadius="full"
                  fontWeight="600"
                >
                  Centro {centro}
                </Badge>
                <Badge
                  bg={BRAND_YELLOW}
                  color={BRAND_GREEN}
                  fontSize="10px"
                  px={2}
                  borderRadius="full"
                  fontWeight="700"
                >
                  Produto {produto}
                </Badge>
              </Flex>
            </Box>
          </Flex>
          <Box h="3px" bg={BRAND_YELLOW} />
        </ModalHeader>

        <ModalCloseButton
          top={3}
          right={3}
          color="white"
          size="sm"
          _hover={{ bg: 'whiteAlpha.300' }}
          zIndex={10}
        />

        {/* ── Body ── */}
        <ModalBody p={6} overflowY="auto">
          <Grid templateColumns={['1fr', '1fr', '1fr 1px 1fr']} gap={0}>
            {/* Saída Média Diária */}
            <Box pr={[0, 0, 5]} pb={[6, 6, 0]}>
              <motion.div
                key={`header-saida-${animKey}`}
                variants={headerVariants}
                initial="hidden"
                animate="visible"
              >
                <Flex align="center" gap={3} mb={4}>
                  <Flex
                    w="40px"
                    h="40px"
                    borderRadius="10px"
                    bg="#EBF8FF"
                    align="center"
                    justify="center"
                    flexShrink={0}
                  >
                    <Icon as={FiTrendingUp} color="#3182CE" boxSize={5} />
                  </Flex>
                  <Box>
                    <Heading fontSize="14px" fontWeight="700" color="gray.800">
                      Saída Média Diária
                    </Heading>
                    <Text fontSize="11px" color="gray.500" mt="1px">
                      Como o valor de venda média diária foi calculado
                    </Text>
                  </Box>
                </Flex>
              </motion.div>

              <StepList
                steps={etapasSaida}
                colorScheme="blue"
                animationKey={`saida-${animKey}`}
              />
            </Box>

            {/* Divider */}
            <Box display={['none', 'none', 'block']}>
              <Divider orientation="vertical" borderColor="gray.200" />
            </Box>
            <Divider display={['block', 'block', 'none']} mb={6} />

            {/* Quantidade Recomendada */}
            <Box pl={[0, 0, 5]}>
              <motion.div
                key={`header-qtd-${animKey}`}
                variants={headerVariants}
                initial="hidden"
                animate="visible"
                style={{ transitionDelay: '0.05s' }}
              >
                <Flex align="center" gap={3} mb={4}>
                  <Flex
                    w="40px"
                    h="40px"
                    borderRadius="10px"
                    bg="#F0FFF4"
                    align="center"
                    justify="center"
                    flexShrink={0}
                  >
                    <Icon
                      as={MdOutlineShoppingCart}
                      color={BRAND_GREEN}
                      boxSize={5}
                    />
                  </Flex>
                  <Box>
                    <Heading fontSize="14px" fontWeight="700" color="gray.800">
                      Quantidade Recomendada
                    </Heading>
                    <Text fontSize="11px" color="gray.500" mt="1px">
                      Como o valor final de recomendação foi obtido
                    </Text>
                  </Box>
                </Flex>
              </motion.div>

              <StepList
                steps={etapasQuantidade}
                colorScheme="green"
                animationKey={`qtd-${animKey}`}
              />
            </Box>
          </Grid>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default CalculoDetalheModal
